import List from "components/common/ListWithMoreOptions";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "components/common/BottomPageSelectorWithPagination";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useWindowSize from "useWindowSize";
import { BSON } from "realm-web";
import useItems from "graphql/useItems";
import { usePaginatedData } from "helpers/hooks";
import useSubcategories, { useCategories } from "graphql/useSubcategories";
import Loading from "components/common/loading";
import { useBusinessContext } from "BusinessApp";
import InputNumber from "../../components/common/InputNumber";

const createDefaultPriceList = (items, t) => {
  const itemsMap = items.map((item) => ({
    itemId: item._id,
    price: item.price,
    _id: new BSON.ObjectId().toString(),
  }));

  return {
    _id: "default",
    name: t("containers.InventoryPriceList.defaultPriceList"),
    items: itemsMap,
    updateDatetime: new Date().toISOString(),
    creationDatetime: new Date().toISOString(),
  };
};

const PriceTable = ({
  priceLists,
  selectList,
  search,
  selectSubcategory,
  selectCategory,
  editing,
  priceListsForEdit,
  setPriceListsForEdit,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const { sorting, setSorting, pagination, setPagination } =
    usePagination("name");

  const { currencyFormat } = useBusinessContext();

  const { items } = useItems();
  const { subcategories, loading: loading1 } = useSubcategories();
  const { categories, loading: loading2 } = useCategories();

  const defaultPriceList = useMemo(
    () => createDefaultPriceList(items, t),
    [items, t]
  );

  const newPriceLists = useMemo(
    () => [defaultPriceList, ...priceLists],
    [defaultPriceList, priceLists]
  );

  const dataFilter = useMemo(() => {
    // eslint-disable-next-line no-undef
    const itemsMap = new Map();
    items.forEach((item) => {
      itemsMap.set(item._id, item);
    });

    const result = newPriceLists.map((priceList) => {
      const priceListItems = priceList?.items?.map((priceListItem) => {
        const item = itemsMap.get(priceListItem.itemId);

        const subcategory = subcategories.find(
          (subcategory) => subcategory?._id === item?.subcategoryId
        );

        const category = categories.find(
          (category) => category?._id === subcategory?.categoryId
        );

        return {
          ...item,
          price: priceListItem.price,
          subcategoryName: subcategory?.name,
          categoryName: category?.name,
          categoryId: category?._id,
        };
      });
      return {
        ...priceList,
        items: priceListItems,
      };
    });

    const filteredResult = result.filter((priceList) => {
      if (selectList && priceList._id !== selectList) {
        return false;
      }
      return true;
    });

    return filteredResult;
  }, [
    categories,
    items,
    priceLists,
    selectList,
    subcategories,
    defaultPriceList,
    newPriceLists,
  ]);

  const data = useMemo(() => {
    const searchLower = search.toLocaleLowerCase();

    return Object.values(items)
      .filter((item) => {
        const matchesSearch = item.name
          .toLocaleLowerCase()
          .includes(searchLower);
        if (!matchesSearch) return false;

        const subcategory = subcategories.find(
          (subcategory) => subcategory?._id === item?.subcategoryId
        );
        const category = categories.find(
          (category) => category?._id === subcategory?.categoryId
        );

        const matchesCategory =
          !selectCategory || (category && category._id === selectCategory);
        const matchesSubcategory =
          !selectSubcategory ||
          (subcategory && subcategory._id === selectSubcategory);

        return matchesCategory && matchesSubcategory;
      })
      .map((item) => {
        const subcategory = subcategories.find(
          (subcategory) => subcategory?._id === item?.subcategoryId
        );

        const category = categories.find(
          (category) => category?._id === subcategory?.categoryId
        );

        return { ...item, subcategory, category };
      });
  }, [
    categories,
    items,
    newPriceLists,
    search,
    subcategories,
    selectCategory,
    selectSubcategory,
  ]);

  const dataToShow = usePaginatedData(data, pagination, sorting, (item) => {
    const itemFind = [];
    dataFilter.forEach((priceList) => {
      const foundItem = priceList.items?.find(
        (priceListItem) => priceListItem._id === item._id
      );
      itemFind.push({
        price: foundItem?.price || "-",
        _id: foundItem?._id || "-",
        priceList: { ...priceList },
        itemId: item._id,
      });
    });

    const subcategory = subcategories.find(
      (subcategory) => subcategory?._id === item?.subcategoryId
    );

    const category = categories.find(
      (category) => category?._id === subcategory?.categoryId
    );

    const buttonList = itemFind.map((item) => {
      if (editing) {
        const updatedItem = priceListsForEdit
          .find((priceList) => priceList._id === item.priceList._id)
          ?.items.find((priceListItem) => priceListItem.itemId === item.itemId);
        return (
          <InputNumber
            key={item._id}
            style={{ fontSize: "12px", opacity: "1", borderRadius: "5px" }}
            value={updatedItem?.price}
            onChange={(value) => {
              let updatedPriceLists = [...priceListsForEdit];

              let foundItemToUpdate = false;
              updatedPriceLists = updatedPriceLists.map((priceList) => {
                if (priceList._id === item.priceList._id) {
                  const updatedItems = priceList.items.map((priceListItem) => {
                    if (priceListItem.itemId === item.itemId) {
                      foundItemToUpdate = true;
                      return {
                        ...priceListItem,
                        price: value
                          ? `${value}`
                          : priceList._id === "default"
                          ? "0"
                          : "",
                      };
                    }
                    return priceListItem;
                  });

                  const items = updatedItems.filter(
                    (item) => item.price !== ""
                  );

                  return {
                    ...priceList,
                    items: [...items],
                  };
                }

                return priceList;
              });

              if (!foundItemToUpdate) {
                const newItem = {
                  itemId: item.itemId,
                  price: `${value}`,
                  _id: new BSON.ObjectId().toString(),
                };

                const priceListToUpdateIndex = updatedPriceLists.findIndex(
                  (priceList) => priceList._id === item.priceList._id
                );

                if (priceListToUpdateIndex !== -1) {
                  updatedPriceLists[priceListToUpdateIndex] = {
                    ...updatedPriceLists[priceListToUpdateIndex],
                    items: [
                      ...updatedPriceLists[priceListToUpdateIndex].items,
                      { ...newItem },
                    ],
                  };
                }
              }

              setPriceListsForEdit(updatedPriceLists);
            }}
          />
        );
      }
      return isNaN(item.price) ? "-" : currencyFormat(item.price);
    });
    return [
      item.sku,
      item.name,
      category?.name,
      subcategory?.name,
      moment(item.updateDatetime).format("lll"),
      ...buttonList,
    ];
  });
  const columns = useMemo(() => {
    const priceListColumns = dataFilter.map((priceList) => ({
      title: priceList.name,
      sortable: true,
      columnId: "price",
    }));
    return [
      {
        title: t("containers.InventoryPriceList.sku"),
        sortable: true,
        columnId: "sku",
      },
      {
        title: t("containers.InventoryPriceList.nameItem"),
        sortable: true,
        columnId: "name",
      },
      {
        title: t("containers.InventoryPriceList.category"),
        sortable: true,
        columnId: "category.name",
      },
      {
        title: t("containers.InventoryPriceList.subCategory"),
        sortable: true,
        columnId: "subcategory.name",
      },
      {
        title: t("containers.InventoryPriceList.InventoryPriceList"),
        sortable: true,
        columnId: "updateDatetime",
      },
      ...priceListColumns,
    ];
  }, [dataFilter]);

  const columnWidths = `${Array(columns.length - 1)
    .fill(`${width <= 990 ? " 6fr" : " 10fr"}`)
    .join(" ")} 7fr`;

  const loading = loading1 || loading2;

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          <List
            width={`${650 + priceLists.length * 125}px`}
            data={dataToShow}
            columns={columns}
            sorting={sorting}
            moreOption={false}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{
              gridTemplateColumns: columnWidths,
            }}
          />
          <BottomPageSelectorWithPagination
            pagination={pagination}
            setPagination={setPagination}
            length={data.length}
          />
        </>
      )}
    </>
  );
};

export default PriceTable;
